import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import { Collapse, InputAdornment, MenuItem, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";

import useGetAllPlatforms from "@/hooks/requests/platform/use-get-all-platform";
import useGetServiceQueues from "@/hooks/requests/service-queue/use-get-service-queues";
import useGetSubjects from "@/hooks/requests/subject/use-get-subjects";
import useGetUsers from "@/hooks/requests/user/use-get-users";
import useGetAllWorkflows from "@/hooks/requests/wokflow/use-get-all-workflow";
import { ConversationReportMessages } from "@/messages/conversation-report/conversation-report.messages";
import { ConversationReportForm } from "@/types/conversation-report.types";

type Props = {
  form: ConversationReportForm;
  messages: ConversationReportMessages;
  showMore: boolean;
  onChange: (patch: Partial<ConversationReportForm>) => void;
};

type ChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

const FIELD_SIZE = { xs: 12, sm: 6, md: 4, lg: 2 };

const COMBO_PAGE = { pageNumber: 1, pageSize: 200 };

export const fieldSx = {
  "& .MuiSelect-icon": { display: "block !important", right: 8, color: "dark.main" },
} as const;

function optionLabel(value: string | null | undefined, fallback: string) {
  return value?.trim() || fallback;
}

type SelectProps = {
  label: string;
  value: string;
  allLabel: string;
  onChange: (event: ChangeEvent) => void;
  children?: React.ReactNode;
};

function SelectField({ label, value, allLabel, onChange, children }: SelectProps) {
  return (
      <TextField
        select
        fullWidth
        label={label}
        value={value}
        onChange={onChange}
        sx={fieldSx}
        slotProps={{
          inputLabel: { shrink: true },
          select: {
            displayEmpty: true,
            IconComponent: KeyboardArrowDownIcon,
          },
        }}
      >
        <MenuItem value="">{allLabel}</MenuItem>
        {children}
      </TextField>
  );
}

function ConversationReportFilters({ form, messages, showMore, onChange }: Props) {
  const platforms = useGetAllPlatforms(COMBO_PAGE);
  const workflows = useGetAllWorkflows(COMBO_PAGE);
  const queues = useGetServiceQueues(COMBO_PAGE);
  const subjects = useGetSubjects(COMBO_PAGE);
  const users = useGetUsers({ ...COMBO_PAGE, filters: [] });

  const set = (field: keyof ConversationReportForm) => (event: ChangeEvent) => {
    onChange({ [field]: event.target.value } as Partial<ConversationReportForm>);
  };

  const yesNo = [
    <MenuItem key="true" value="true">
      {messages.yes}
    </MenuItem>,
    <MenuItem key="false" value="false">
      {messages.no}
    </MenuItem>,
  ];

  const six = FIELD_SIZE;
  const rangeProps = (fromKey: keyof ConversationReportForm, toKey: keyof ConversationReportForm) => ({
    from: String(form[fromKey] ?? ""),
    to: String(form[toKey] ?? ""),
    onFrom: set(fromKey),
    onTo: set(toKey),
    fromLabel: messages.from,
    toLabel: messages.to,
  });

  return (
    <>
      <Grid container spacing={3}>
      <Grid size={{ xs: 12, sm: 12, md: 8, lg: 4 }}>
        <TextField
          fullWidth
          label={messages.contact}
          placeholder={messages.contactPlaceholder}
          value={form.contactText}
          onChange={set("contactText")}
          sx={fieldSx}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Grid>
      <Grid size={six}>
        <SelectField label={messages.mainSubject} value={form.mainSubjectId} allLabel={messages.all} onChange={set("mainSubjectId")}>
          {subjects.data?.items.map((item) => (
            <MenuItem key={item.id} value={String(item.id)}>
              {item.subject}
            </MenuItem>
          ))}
        </SelectField>
      </Grid>
      <Grid size={six}>
        <SelectField label={messages.conversationStatus} value={form.status} allLabel={messages.all} onChange={set("status")}>
          <MenuItem value="attending">{messages.statusAttending}</MenuItem>
          <MenuItem value="waiting">{messages.statusWaiting}</MenuItem>
          <MenuItem value="closed">{messages.statusClosed}</MenuItem>
          <MenuItem value="reactivated">{messages.statusReactivated}</MenuItem>
        </SelectField>
      </Grid>
      <Grid size={six}>
        <SelectField label={messages.attendant} value={form.userId} allLabel={messages.all} onChange={set("userId")}>
          {users.data?.items.map((item) => (
            <MenuItem key={item.id} value={String(item.id)}>
              {item.name}
            </MenuItem>
          ))}
        </SelectField>
      </Grid>
      <Grid size={six}>
        <SelectField label={messages.supportByHuman} value={form.supportByHuman} allLabel={messages.all} onChange={set("supportByHuman")}>
          {yesNo}
        </SelectField>
      </Grid>
      <Grid size={six}>
        <SelectField label={messages.closedByHuman} value={form.closedByHuman} allLabel={messages.all} onChange={set("closedByHuman")}>
          {yesNo}
        </SelectField>
      </Grid>
      <Grid size={six}>
        <SelectField label={messages.issueSolved} value={form.issueSolved} allLabel={messages.all} onChange={set("issueSolved")}>
          {yesNo}
        </SelectField>
      </Grid>
      <DateRange label={messages.startDate} {...rangeProps("startDateFrom", "startDateTo")} />
      <DateRange label={messages.endDate} {...rangeProps("endDateFrom", "endDateTo")} />
      </Grid>
      <Collapse in={showMore} timeout={350}>
        <Grid container spacing={3} pt={3}>
      <Grid size={six}>
        <SelectField label={messages.platform} value={form.platformId} allLabel={messages.all} onChange={set("platformId")}>
          {platforms.data?.items.map((item) => (
            <MenuItem key={item.id} value={String(item.id)}>
              {optionLabel(item.displayName || item.name, String(item.id))}
            </MenuItem>
          ))}
        </SelectField>
      </Grid>
      <Grid size={six}>
        <SelectField label={messages.workflow} value={form.workflowId} allLabel={messages.all} onChange={set("workflowId")}>
          {workflows.data?.items.map((item) => (
            <MenuItem key={item.id} value={String(item.id)}>
              {optionLabel(item.displayName || item.name, String(item.id))}
            </MenuItem>
          ))}
        </SelectField>
      </Grid>
      <Grid size={six}>
        <SelectField label={messages.queue} value={form.customerServiceQueueId} allLabel={messages.all} onChange={set("customerServiceQueueId")}>
          {queues.data?.items.map((item) => (
            <MenuItem key={item.id} value={String(item.id)}>
              {item.name}
            </MenuItem>
          ))}
        </SelectField>
      </Grid>
      <Grid size={six}>
        <SelectField label={messages.hasUnread} value={form.hasUnread} allLabel={messages.all} onChange={set("hasUnread")}>
          {yesNo}
        </SelectField>
      </Grid>
      <Grid size={six}>
        <TextField fullWidth label={messages.phone} value={form.mobilePhone} onChange={set("mobilePhone")} slotProps={{ inputLabel: { shrink: true } }} />
      </Grid>
      <Grid size={six}>
        <SelectField label={messages.csat} value={form.csat} allLabel={messages.all} onChange={set("csat")}>
          <MenuItem value="1">1</MenuItem>
          <MenuItem value="2">2</MenuItem>
          <MenuItem value="3">3</MenuItem>
          <MenuItem value="4">4</MenuItem>
          <MenuItem value="5">5</MenuItem>
          <MenuItem value="none">{messages.withoutCsat}</MenuItem>
        </SelectField>
      </Grid>
      <DateRange label={messages.lastReceivedDate} {...rangeProps("lastReceivedDateFrom", "lastReceivedDateTo")} />
      <DateRange label={messages.lastInterationDate} {...rangeProps("lastInterationDateFrom", "lastInterationDateTo")} />
      <DateRange label={messages.firstAnswerDate} {...rangeProps("firstAnswerDateFrom", "firstAnswerDateTo")} />
      <DateRange label={messages.lastReactivation} {...rangeProps("lastReactivationAtFrom", "lastReactivationAtTo")} />
      <DateRange label={messages.lastReadDate} {...rangeProps("lastReadDateFrom", "lastReadDateTo")} />
      <DateRange label={messages.deletedDate} {...rangeProps("deletedDateFrom", "deletedDateTo")} />
        </Grid>
      </Collapse>
    </>
  );
}

type DateRangeProps = {
  label: string;
  from: string;
  to: string;
  fromLabel: string;
  toLabel: string;
  onFrom: (event: ChangeEvent) => void;
  onTo: (event: ChangeEvent) => void;
};

function DateRange({ label, from, to, fromLabel, toLabel, onFrom, onTo }: DateRangeProps) {
  return (
    <>
      <Grid size={FIELD_SIZE}>
        <TextField
          fullWidth
          type="date"
          label={`${label} (${fromLabel})`}
          value={from}
          onChange={onFrom}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Grid>
      <Grid size={FIELD_SIZE}>
        <TextField
          fullWidth
          type="date"
          label={`${label} (${toLabel})`}
          value={to}
          onChange={onTo}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Grid>
    </>
  );
}

export default ConversationReportFilters;
